import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Edit3 } from "lucide-react";
import { PdfField, EditorMode } from "../types";
import { FieldForm } from "./FieldForm";
import { AcceptedDestinationPacketFields, signerOptions } from "@/lib/api/destinationPackets";

interface FieldsListProps {
  fields: PdfField[];
  selectedField: string | null;
  onSelectField: (fieldId: string | null) => void;
  onUpdateField: (fieldId: string, updates: Partial<PdfField>) => void;
  onDeleteField: (fieldId: string) => void;
  onDuplicateField?: (fieldId: string) => void;
  mode: EditorMode;
}

export function FieldsList({
  fields,
  selectedField,
  onSelectField,
  onUpdateField,
  onDeleteField,
  onDuplicateField,
  mode,
}: FieldsListProps) {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});

  // Handle keyboard shortcut for duplicating fields
  useEffect(() => {
    if (mode !== 'edit' || !onDuplicateField || !selectedField) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+V (Windows/Linux) or Cmd+V (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        onDuplicateField(selectedField);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [mode, onDuplicateField, selectedField]);

  const handleEditField = (fieldId: string) => {
    setEditingField(fieldId);
    onSelectField(fieldId);
  };

  const handleSaveField = (fieldId: string, updates: Partial<PdfField>) => {
    onUpdateField(fieldId, updates);
    setEditingField(null);
  };

  const handlePreviewValueChange = (fieldId: string, value: string) => {
    setPreviewValues(prev => ({ ...prev, [fieldId]: value }));
    onUpdateField(fieldId, { value });
  };

  const systemFields = fields.filter(field => field.field_type === 'system');
  const signatureFields = fields.filter(field => field.field_type === 'signature');

  const getAvailableFields = (excludeFieldId?: string): AcceptedDestinationPacketFields[] => {
    const usedFieldIds = fields
      .filter(field => field.field_id !== excludeFieldId)
      .map(field => field.field_id as AcceptedDestinationPacketFields);

    return Object.values(AcceptedDestinationPacketFields).filter(field => !usedFieldIds.includes(field));
  };

  return (
    <div className="h-full p-4 space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">PDF Fields</h2>
      </div>

      {fields.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-8">
          No fields created yet.<br />
          {mode === 'edit' ? 'Draw rectangles on the PDF to create fields.' : ''}
        </p>
      )}

      {
        systemFields.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">System Fields ({systemFields.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {systemFields.map((field) => (
                <div key={field.field_id}>
                  {editingField === field.field_id ? (
                    <FieldForm
                      field={field}
                      onSave={(updates) => handleSaveField(field.field_id, updates)}
                      onCancel={() => setEditingField(null)}
                      availableFields={getAvailableFields(field.field_id)}
                    />
                  ) : (
                    <div
                      className={`px-2 py-1.5 border rounded-lg cursor-pointer transition-colors ${selectedField === field.field_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => onSelectField(field.field_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm">{field.field_key || field.field_id}</span>
                          <span className="text-xs px-1 py-0.5 bg-green-100 text-green-800 rounded">
                            System
                          </span>
                          <span className="text-xs text-gray-500">Page {field.position_data.page}</span>
                        </div>

                        {mode !== 'display' && (
                          <div className="flex gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(field.field_id);
                              }}
                            >
                              <Edit3 className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteField(field.field_id);
                              }}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-3">
                        <div className="leading-tight">
                          Position: ({(field.position_data.x * 100).toFixed(1)}%, {(field.position_data.y * 100).toFixed(1)}%)
                        </div>
                        <div className="leading-tight">
                          Size: {(field.position_data.width * 100).toFixed(1)}% × {(field.position_data.height * 100).toFixed(1)}%
                        </div>
                        <div className="leading-tight">Font Size: {field.position_data.text_size}px</div>
                      </div>

                      {mode === 'edit' && (
                        <div className="mt-1.5 flex items-center gap-2">
                          <Label className="text-xs whitespace-nowrap">Sample Value:</Label>
                          <Input
                            placeholder="Enter text to preview"
                            value={previewValues[field.field_id] || field.value || ''}
                            onChange={(e) => handlePreviewValueChange(field.field_id, e.target.value)}
                            className="h-7 flex-1"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )
      }

      {
        signatureFields.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Eversign Fields ({signatureFields.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {signatureFields.map((field) => (
                <div key={field.field_id}>
                  {editingField === field.field_id ? (
                    <FieldForm
                      field={field}
                      onSave={(updates) => handleSaveField(field.field_id, updates)}
                      onCancel={() => setEditingField(null)}
                      availableFields={getAvailableFields(field.field_id)}
                    />
                  ) : (
                    <div
                      className={`px-2 py-1.5 border rounded-lg cursor-pointer transition-colors ${selectedField === field.field_id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                        }`}
                      onClick={() => onSelectField(field.field_id)}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-sm">{field.field_key || field.field_id}</span>
                          <span className="text-xs px-1 py-0.5 bg-purple-100 text-purple-800 rounded">
                            Signature
                          </span>
                          <span className="text-xs text-gray-500">Page {field.position_data.page}</span>
                        </div>
                        {mode !== 'display' && (
                          <div className="flex gap-0.5">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditField(field.field_id);
                              }}
                            >
                              <Edit3 className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                onDeleteField(field.field_id);
                              }}
                            >
                              <Trash2 className="w-3 h-3 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="text-xs text-gray-600 grid grid-cols-2 gap-x-3">
                        <div className="leading-tight">
                          Position: ({(field.position_data.x * 100).toFixed(1)}%, {(field.position_data.y * 100).toFixed(1)}%)
                        </div>
                        <div className="leading-tight">
                          Size: {(field.position_data.width * 100).toFixed(1)}% × {(field.position_data.height * 100).toFixed(1)}%
                        </div>
                        {field.field_dest_type ? (
                          <div className="leading-tight">Type: {field.field_dest_type}</div>
                        ) : (
                          <div className="leading-tight">Type: signature</div>
                        )}
                        {field.signer_id && (
                          <div className="leading-tight">Signer: {signerOptions.find(opt => opt.value === field.signer_id)?.label || field.signer_id}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )
      }
    </div >
  );
}

